import React, { useState } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  useTheme,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Box,
  Button,
} from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import MenuIcon from "@mui/icons-material/Menu";
import HomeIcon from "@mui/icons-material/Home";
import PricingIcon from "@mui/icons-material/MonetizationOn";
import ContactIcon from "@mui/icons-material/ContactMail";
import FaqIcon from "@mui/icons-material/HelpOutline";
import DemoIcon from "@mui/icons-material/Slideshow";
import AboutIcon from "@mui/icons-material/InfoOutlined";
import SecurityIcon from "@mui/icons-material/GppGoodOutlined";
import PrivacyIcon from "@mui/icons-material/PrivacyTipOutlined";
import TermsIcon from "@mui/icons-material/GavelOutlined";
import AccessibilityIcon from "@mui/icons-material/AccessibilityNew";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";

/**
 * Primary navigation. Kept to six so the desktop bar does not wrap and the mobile drawer stays
 * scannable: the trust and legal pages live in the footer instead, which is where a visitor looks
 * for them and which keeps them crawlable without spending top-level attention on them.
 */
export const navItems = [
  { text: "Home", icon: <HomeIcon />, link: "/" },
  { text: "Pricing", icon: <PricingIcon />, link: "/pricing" },
  { text: "Demo", icon: <DemoIcon />, link: "/demo" },
  { text: "FAQs", icon: <FaqIcon />, link: "/faqs" },
  { text: "About", icon: <AboutIcon />, link: "/about" },
  { text: "Contact", icon: <ContactIcon />, link: "/contact" },
];

/** Trust and legal pages. Footer only, plus the mobile drawer where there is room to list them. */
export const legalNavItems = [
  { text: "Security", icon: <SecurityIcon />, link: "/security" },
  { text: "Privacy", icon: <PrivacyIcon />, link: "/privacy" },
  { text: "Terms", icon: <TermsIcon />, link: "/terms" },
  {
    text: "Accessibility",
    icon: <AccessibilityIcon />,
    link: "/accessibility",
  },
];

/**
 * The drawer's panel, named so the button that opens it can point `aria-controls` at something real.
 */
const DRAWER_PANEL_ID = "mobile-navigation";

const Navbar = () => {
  const theme = useTheme();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const navigate = useNavigate();
  const handleDrawerOpen = () => {
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
  };

  return (
    <>
      <AppBar
        position="static"
        color="default"
        style={{ backgroundColor: theme.palette.background.paper }}
      >
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            aria-label="Open navigation menu"
            aria-expanded={drawerOpen}
            aria-controls={DRAWER_PANEL_ID}
            onClick={handleDrawerOpen}
            sx={{ display: { xs: "inline-flex", md: "none" } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography
            variant="h6"
            component={Link}
            to="/"
            style={{
              flexGrow: 1,
              color: theme.palette.text.primary,
              textDecoration: "none",
            }}
            onClick={() => navigate("/")}
          >
            BadgeReg
          </Typography>
          {/* The drawer is unmounted until opened, so these desktop links are the only
              internal links present in the prerendered HTML that a crawler can follow. */}
          <Box
            component="nav"
            aria-label="Main"
            sx={{ display: { xs: "none", md: "flex" }, gap: 0.5 }}
          >
            {navItems.map((item) => (
              <Button
                key={item.text}
                component={Link}
                to={item.link}
                color="inherit"
                sx={{ color: theme.palette.text.primary }}
              >
                {item.text}
              </Button>
            ))}
          </Box>
        </Toolbar>
      </AppBar>
      <Drawer anchor="left" open={drawerOpen} onClose={handleDrawerClose}>
        <Box id={DRAWER_PANEL_ID} sx={{ width: 250 }}>
          <IconButton onClick={handleDrawerClose} aria-label="Close navigation">
            <ChevronLeftIcon />
          </IconButton>
          <Divider />
          <List component="nav" aria-label="Main (mobile)">
            {navItems.map((item) => (
              <ListItem
                button
                key={item.text}
                onClick={handleDrawerClose}
                component={Link}
                to={item.link}
                style={{ textDecoration: "none", color: "inherit" }}
              >
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText primary={item.text} />
              </ListItem>
            ))}
          </List>
          <Divider />
          <List component="nav" aria-label="Trust and legal (mobile)" dense>
            {legalNavItems.map((item) => (
              <ListItem
                button
                key={item.text}
                onClick={handleDrawerClose}
                component={Link}
                to={item.link}
                style={{ textDecoration: "none", color: "inherit" }}
              >
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{ variant: "body2" }}
                />
              </ListItem>
            ))}
          </List>
        </Box>
      </Drawer>
    </>
  );
};

export default Navbar;
